import { ChatConfig } from '../types';

// ========== Constants ==========
/**
 * Default branding colors used when no custom branding is available
 */
const DEFAULT_COLORS = {
  primary: '#3b82f6',
  secondary: '#6b7280',
  background: '#f5f5f5',
  text: '#333333'
};

// ========== Types ==========
/**
 * Available color keys for branding customization
 */
type ColorKey = 'primary' | 'secondary' | 'background' | 'text';

/**
 * Available font types for branding customization
 */
type FontType = 'primary' | 'secondary';

// ========== Utility Functions ==========

/**
 * Helper to safely access branding colors with fallbacks
 * @param config - The chat configuration object
 * @param colorKey - The color key to retrieve
 * @returns The color value or a default fallback
 */
export const getBrandingColor = (config: ChatConfig | null, colorKey: ColorKey): string => {
  if (!config || !config.branding || !config.branding.colors) {
    return DEFAULT_COLORS[colorKey];
  }
  
  // Check if the key exists in the colors object
  if (colorKey in config.branding.colors) {
    // Safe to access since we've verified the key exists
    return config.branding.colors[colorKey as keyof typeof config.branding.colors] || DEFAULT_COLORS[colorKey];
  }
  
  return DEFAULT_COLORS[colorKey];
};

/**
 * Helper to safely access branding fonts with fallbacks
 * @param config - The chat configuration object
 * @param fontType - The font type to retrieve
 * @returns The font name or a default fallback
 */
export const getBrandingFont = (config: ChatConfig | null, fontType: FontType): string => {
  if (!config || !config.branding || !config.branding.fonts) {
    return fontType === 'primary' ? 'Inter' : 'system-ui';
  }
  
  // Check if the key exists in the fonts object
  if (fontType in config.branding.fonts) {
    // Safe to access since we've verified the key exists
    return config.branding.fonts[fontType as keyof typeof config.branding.fonts] || 
      (fontType === 'primary' ? 'Inter' : 'system-ui');
  }
  
  return fontType === 'primary' ? 'Inter' : 'system-ui';
};

/**
 * Helper to sanitize font names for URL
 * @param name - Font name to sanitize
 * @returns Sanitized font name for Google Fonts URL
 */
const sanitizeFontName = (name: string) => name.replace(/\s+/g, '+');

/**
 * Loads Google Fonts for the branding
 * @param primaryFont - Primary font to load
 * @param secondaryFont - Secondary font to load
 */
export const loadGoogleFonts = (primaryFont: string, secondaryFont: string) => {
  // Check if we've already loaded fonts in this session
  const loadedFonts = sessionStorage.getItem('loaded-fonts');
  if (loadedFonts === `${primaryFont},${secondaryFont}`) {
    return;
  }
  
  // Create a link element for the Google Fonts
  const fontLink = document.createElement('link');
  fontLink.rel = 'stylesheet';
  fontLink.href = `https://fonts.googleapis.com/css2?family=${sanitizeFontName(primaryFont)}:wght@400;500;700&family=${sanitizeFontName(secondaryFont)}:wght@400;500;700&display=swap`;
  
  // Add the link to the head
  document.head.appendChild(fontLink);
  
  // Mark these fonts as loaded
  sessionStorage.setItem('loaded-fonts', `${primaryFont},${secondaryFont}`);
}; 